import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, Form, Input, Select, Button, Space, message, Alert } from 'antd'
import { adminUsersApi } from '@/api/admin-users'
import type { UpdateAdminUserInput } from '@/types/admin-user'
import { PageLoading } from '@/components/PageLoading'
import { getApiErrorMessage } from '@/utils/error'

export const AdminUserEdit = () => {
  const navigate = useNavigate()
  const { email } = useParams<{ email: string }>()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  // Fetch user data (enabled only when email exists)
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user', email],
    queryFn: () => adminUsersApi.getAdminUser(email!),
    enabled: !!email,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (values: UpdateAdminUserInput) => adminUsersApi.updateAdminUser(email!, values),
    onSuccess: () => {
      message.success('Admin user updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', email] })
      navigate('/admin/users')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update admin user'))
    },
  })

  // Pre-fill form with user data
  useEffect(() => {
    if (data?.data.user) {
      form.setFieldsValue({
        email: data.data.user.email,
        given_name: data.data.user.given_name,
        family_name: data.data.user.family_name,
        role: data.data.user.role,
      })
    }
  }, [data, form])

  // Handle missing email after all hooks
  if (!email) {
    navigate('/admin/users')
    return null
  }

  const onFinish = (values: UpdateAdminUserInput) => {
    updateMutation.mutate(values)
  }

  if (isLoading) {
    return <PageLoading />
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="Error"
          description="Failed to load admin user details"
          type="error"
          showIcon
        />
      </Card>
    )
  }

  return (
    <Card
      title="Edit Admin User"
      extra={<Button onClick={() => navigate('/admin/users')}>Cancel</Button>}
    >
      <Alert
        message="Note"
        description="Email address cannot be changed after user creation. To change the email, delete this user and create a new one."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item label="Email" name="email">
          <Input disabled />
        </Form.Item>

        <Form.Item
          label="First Name"
          name="given_name"
          rules={[{ required: true, message: 'First name is required' }]}
        >
          <Input placeholder="John" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="family_name"
          rules={[{ required: true, message: 'Last name is required' }]}
        >
          <Input placeholder="Doe" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Role is required' }]}
        >
          <Select
            placeholder="Select role"
            options={[
              { label: 'Admin - Full access', value: 'ADMIN' },
              { label: 'Support - Limited access', value: 'SUPPORT' },
              { label: 'Viewer - Read-only access', value: 'VIEWER' },
            ]}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
              Update Admin User
            </Button>
            <Button onClick={() => navigate('/admin/users')}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}
